import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { bankingAPI, purchasePaymentsAPI, purchasesAPI } from '../../../api';
import { useToast } from '../../../hooks/useToast';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { FormLabel } from '../../../components/ui/FormLabel';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(Number(amount || 0));

export default function MobilePurchasePaymentPrompt() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const purchaseId = searchParams.get('purchaseId');
  const { success, error: showError, ToastContainer } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recordNow, setRecordNow] = useState(false);
  const [purchase, setPurchase] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [formData, setFormData] = useState({
    bank_account_id: '',
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
  });

  const balanceDue = useMemo(() => Number(purchase?.balanceDue || 0), [purchase]);

  useEffect(() => {
    const bootstrap = async () => {
      if (!purchaseId) {
        navigate('/tenant/mobile', { replace: true });
        return;
      }

      try {
        setLoading(true);
        const [purchaseData, accounts] = await Promise.all([
          purchasesAPI.getOne(purchaseId),
          bankingAPI.getAccounts({ include_inactive: false }),
        ]);
        const accountList = Array.isArray(accounts) ? accounts : [];
        setPurchase(purchaseData);
        setBankAccounts(accountList);
        setFormData((prev) => ({
          ...prev,
          bank_account_id: accountList[0]?.id || '',
          amount: Number(purchaseData?.balanceDue || 0).toFixed(2),
        }));
      } catch (err) {
        showError('Could not load payment details', err.message || 'Please try again');
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, [navigate, purchaseId, showError]);

  const handleSkip = () => {
    navigate('/tenant/mobile', { replace: true });
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!formData.bank_account_id) {
      showError('Bank account is required');
      return;
    }
    const amount = Number(formData.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      showError('Enter a valid payment amount');
      return;
    }
    if (amount > balanceDue) {
      showError(`Payment cannot exceed balance due of ${formatCurrency(balanceDue)}`);
      return;
    }

    try {
      setSaving(true);
      await purchasePaymentsAPI.create({
        purchase_id: purchaseId,
        bank_account_id: formData.bank_account_id,
        amount: amount.toString(),
        payment_method: 'manual',
        payment_date: formData.payment_date,
      });
      success('Payment recorded');
      navigate('/tenant/mobile', { replace: true });
    } catch (err) {
      showError('Failed to record payment', err.message || 'Please try again');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="px-4 py-6 pb-8">
        <p className="text-sm text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 pb-8">
      <ToastContainer />
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
        Purchase saved
      </h1>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Would you like to record a payment against this purchase now?
      </p>

      <div className="mb-6 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          <span className="font-medium">Purchase:</span> {purchase?.purchaseNum || '—'}
        </p>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          <span className="font-medium">Balance due:</span> {formatCurrency(balanceDue)}
        </p>
      </div>

      {!recordNow ? (
        <div className="grid grid-cols-2 gap-3">
          <Button type="button" variant="outline" className="min-h-[44px]" onClick={handleSkip}>
            Skip
          </Button>
          <Button
            type="button"
            variant="primary"
            className="min-h-[44px]"
            onClick={() => setRecordNow(true)}
            disabled={balanceDue <= 0}
          >
            Record payment
          </Button>
        </div>
      ) : (
        <form onSubmit={handleRecordPayment} className="space-y-4">
          <div>
            <FormLabel required className="text-gray-700 dark:text-gray-300">
              Bank account
            </FormLabel>
            <select
              value={formData.bank_account_id}
              onChange={(e) => setFormData((prev) => ({ ...prev, bank_account_id: e.target.value }))}
              required
              className="min-h-[44px] w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select bank account</option>
              {bankAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.account_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <FormLabel required className="text-gray-700 dark:text-gray-300">
              Payment amount
            </FormLabel>
            <Input
              type="number"
              min="0.01"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData((prev) => ({ ...prev, amount: e.target.value }))}
              required
              className="min-h-[44px] border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Payment date</FormLabel>
            <Input
              type="date"
              value={formData.payment_date}
              onChange={(e) => setFormData((prev) => ({ ...prev, payment_date: e.target.value }))}
              className="min-h-[44px] border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="min-h-[44px]"
              onClick={handleSkip}
              disabled={saving}
            >
              Skip
            </Button>
            <Button type="submit" variant="primary" className="min-h-[44px]" disabled={saving}>
              {saving ? 'Saving...' : 'Save payment'}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
